import { BuildStatus } from '@cadstrom/cadstrom-shared';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';

import { useShowBillingFlow } from '#components/feature-flag/use-show-billing-flow.js';
import { ProjectName } from '#components/project-name/project-name.js';
import { RelativeDate } from '#components/relative-timestamp/relative-date.js';
import { ProjectRowActionsMenu } from './project-row-actions-menu.js';
import { ProjectStatusIndicator } from './project-status-indicator.js';
import { ProjectTriageRowAction } from './project-triage-row-action.js';

import type { ProjectTriageItem } from './project-triage-utils.js';

export interface ProjectTriageRowProps {
	project: ProjectTriageItem;
	orgName: string;
	/** The lower-priority "All projects" group renders more quietly and slightly tighter. */
	dimmed?: boolean;
}

/**
 * A single project in the triage list: name (linking to the overview) and meta
 * on the left, status in the middle, and the one next-action plus overflow menu
 * on the right.
 */
export function ProjectTriageRow({ project, orgName, dimmed = false }: ProjectTriageRowProps) {
	const { id, name, latestRevisionName, status, updatedAt, isDemo } = project;
	const isComplete = status === BuildStatus.Complete;
	const showBillingFlow = useShowBillingFlow(orgName);

	return (
		<div
			className={clsx(
				'grid grid-cols-[1fr_auto] items-center gap-3.5 px-3.5 transition-colors sm:grid-cols-[1fr_168px_156px]',
				dimmed ? 'py-3' : 'py-3.5',
				isComplete ? 'bg-green-a2 hover:bg-green-a3' : 'hover:bg-gray-a2',
			)}
		>
			<div className='flex min-w-0 flex-col gap-1.5'>
				<ProjectName isDemo={isDemo}>
					<Link
						from='/orgs/$orgName/projects/'
						to='./$projectId'
						params={{ projectId: id }}
						className={clsx(
							'truncate min-w-0 text-lg leading-tight hover:underline',
							dimmed && !isComplete ? 'font-medium text-gray-a11' : 'font-semibold text-gray-a12',
						)}
					>
						{name}
					</Link>
				</ProjectName>
				<span className='flex min-w-0 items-center gap-1.5 text-xs text-gray-a10'>
					{latestRevisionName !== undefined && (
						<>
							<span className='font-mono'>{latestRevisionName}</span>
							<span aria-hidden>·</span>
						</>
					)}
					<span className='whitespace-nowrap'>
						updated <RelativeDate date={updatedAt === undefined ? undefined : new Date(updatedAt)} />
					</span>
				</span>
			</div>

			<div className='hidden sm:flex'>
				<ProjectStatusIndicator
					status={status}
					hasUploadedFiles={project.hasUploadedFiles}
					automationMode={project.automationMode}
					showBillingFlow={showBillingFlow}
				/>
			</div>

			<div className='flex items-center justify-end gap-1.75'>
				<ProjectTriageRowAction project={project} orgName={orgName} />
				<ProjectRowActionsMenu projectId={id} projectName={name} />
			</div>
		</div>
	);
}
